function runExtend() {
    // ============================================================================
    // EXTEND INTEGRATION
    // ============================================================================

    // available during synchronous execution, not inside async callbacks.
    const _scriptEl = document.currentScript;

    // ============================================================================
    // LOADERS
    // ============================================================================

    function loadScript(src) {
        return new Promise((resolve, reject) => {
            const script   = document.createElement('script');
            script.src     = src;
            script.onload  = resolve;
            script.onerror = () => reject(new Error(`Extend: failed to load script: ${src}`));
            document.head.appendChild(script);
        });
    }

    function loadCSS() {
        const cssHref = _scriptEl?.src?.replace('extend.js', 'extend.css');
        if (!cssHref) return;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssHref;
        document.head.appendChild(link);
    }

    async function loadExtendSDKs() {
        await loadScript('https://sdk.helloextend.com/extend-sdk-client/v1/extend-sdk-client.min.js');
        await loadScript('https://sdk.helloextend.com/extend-sdk-client-shopify-addon/v1/extend-sdk-client-shopify-addon.min.js');
    }

    // ============================================================================
    // GLOBAL VARIABLES & CONFIGURATION
    // ============================================================================

    const storeId = 'a1aaea9f-a388-4276-8d68-5d722acf2a70'; // Extend Admin or Merchant Portal
    const env = 'production';                             // 'production' or 'dev'
    const isMainCart = location.pathname.includes('/cart');
    const regEx = /\d+$/;
    let cart = null; // populated by fetchCart(), shared across all snippets

    // Runtime selectors — populated by setupCheckoutPlusConfiguration()
    const config = {
        isMainCart,
        defaultCheckoutButton: null,
        checkoutPlusButton:    null,
        spLocation:            null,
        spInsertType:          null,
        quoteState:            null,
    };

    // Runtime state for Checkout Plus
    const state = {
        SPOffer:           {},
        originalButton:    null,
        checkoutPlusButton: null,
        buttonsInitialized: false,
    };

    // Checkout+ Selectors -------------------------------------------------------------------------------
    function setupCheckoutPlusConfiguration() {
        if (config.isMainCart) {
            config.defaultCheckoutButton = '#checkout';
            config.checkoutPlusButton    = '.extend-checkout-plus-button';
            config.spLocation            = '.cart__blocks';
            config.spInsertType          = 'before';
        } else {
            config.defaultCheckoutButton = '.qsc2-checkout-button';
            config.checkoutPlusButton    = '.extend-checkout-plus-button-side';
            config.spLocation            = '.cart__summary-row';
            config.spInsertType          = 'before';
        }
    }

    // ============================================================================
    // SHARED UTILITIES
    // ============================================================================

    const mkEl = (tag, cls) => {
        const el = document.createElement(tag);
        if (cls) el.className = cls;
        return el;
    };

    const formatCentsToDollars = (cents) => `$${(cents / 100).toFixed(2)}`;
    const cartType = () => config.isMainCart ? 'main' : 'side';

    async function fetchCart() {
        const res = await fetch('/cart.js');
        cart = await res.json();
        return cart;
    }

    // ============================================================================
    // INITIALIZATION
    // ============================================================================

    function initExtendConfiguration() {
        if (!window.Extend || !window.ExtendShopify) return;

        Extend.config({ storeId, environment: env });

        Extend.integration = {
            aftermarketModal:    true,  // Aftermarket Modal
            currency:            'USD', // USD or CAD
            pdpOffer:            true,  // PDP Visible Offer
            pdpModalOffer:       true,  // PDP Modal Offer
            offerProperties:     {},    // PDP Offer Properties (populated at runtime)
            cartOffer:           false,  // Cart Offers
            sidecartOffer:       false,  // Side Cart Offers
            cartBalancing:       false,  // Cart Balancing / Normalization
            atcOffer:            false,  // ATC Modal Offer
            quickviewOffer:      false,  // Quickview Visible Offer
            quickviewModalOffer: false,  // Quickview Modal Offer
            checkoutPlus:        false,  // Checkout+ with Shipping Protection
        };

        if (Shopify?.currency?.active !== Extend.integration.currency) return;

        Extend.integration.aftermarketModal && initAftermarket();
        Extend.integration.pdpOffer         && initProductPageOffer();
        Extend.integration.atcOffer         && initATCOffer();
        Extend.integration.quickviewOffer   && initQuickviewOffer();

        const needsCart = Extend.integration.cartOffer || Extend.integration.checkoutPlus;
        if (needsCart) {
            fetchCart()
                .then(() => {
                    Extend.integration.cartOffer    && initCartOffer(cart);
                })
                .catch(err => console.error('Extend: could not fetch cart:', err));
        }
    }

    // ============================================================================
    // 5. BOOT
    // CSS loads in parallel with SDKs — neither depends on the other.
    // ============================================================================

    loadCSS();
    loadExtendSDKs()
        .then(initExtendConfiguration)
        .catch(err => console.error('Extend: SDK failed to load:', err));

    // ============================================================================
    // AFTERMARKET
    // ============================================================================

    function initAftermarket() {
        try {
            const params     = new URL(document.location).searchParams;
            const leadToken  = params.get('leadToken') ?? params.get('leadtoken');
            if (!leadToken) return;

            Extend.aftermarketModal.open({
                leadToken,
                onClose(plan, product, quantity) {
                    if (plan && product) {
                        ExtendShopify.addPlanToCart(
                            { plan, product, leadToken, quantity: quantity || 1 },
                            () => { location.href = '/cart'; }
                        );
                    }
                },
            });
        } catch (err) {
            console.error('EXTEND - AFTERMARKET:', err);
        }
    }

    // ============================================================================
    // PDP OFFER
    // ============================================================================

    function initProductPageOffer() {
        
        if (meta.page.pageType !== 'product') return;

        const productForm = document.querySelector('product-form form[action="/cart/add"]'); // THEME SPECIFIC
        if (!productForm) return;

        const addToCartButton  = productForm.querySelector('.single-add-to-cart-button'); // THEME SPECIFIC
        const dispatchSideCart = true;
        const productCategory  = meta?.product?.type ?? null;

        const extendOffer      = mkEl('div', 'extend-offer');
        productForm.prepend(extendOffer);

        function getVariantPrice(variantId) {
            const match = meta?.product?.variants?.find(v => v.id.toString() === variantId.toString());
            return match ? parseInt(match.price) : null;
        }

        async function getOfferProps(variantId, price) {
            const { components } = await ExtendShopify.getBundleComponents({
                variantId: `gid://shopify/ProductVariant/${variantId}`,
            }) || {};
            return components?.length > 0
                ? { referenceId: variantId, products: components }
                : { referenceId: variantId, price, category: productCategory };
        }

        async function initProductOffer() {
            try {
                if (!productForm || !addToCartButton || !extendOffer) {
                    throw new Error('missing productForm, addToCartButton, or extendOffer');
                }

                if (meta?.product?.vendor === 'Extend') addToCartButton.disabled = true;

                let variantId = productForm.querySelector('[name="id"]').value;
                let price     = getVariantPrice(variantId);

                Extend.integration.offerProperties = { variant: variantId, category: productCategory, price };

                Extend.buttons.render(extendOffer, await getOfferProps(variantId, price));

                productForm.addEventListener('change', async function () {
                    try {
                        variantId = productForm.querySelector('[name="id"]').value;
                        if (!variantId) return;

                        price = getVariantPrice(variantId);
                        Extend.integration.offerProperties = { variant: variantId, category: productCategory, price };
                        Extend.setActiveProduct(extendOffer, await getOfferProps(variantId, price));
                    } catch (err) {
                        console.error('EXTEND - VARIANT CHANGE:', err);
                    }
                });

                function handleAddToCart(e) {
                    e.preventDefault();
                    e.stopImmediatePropagation();

                    const quantity = productForm.querySelector('[name="quantity"]')?.value || 1;

                    ExtendShopify.handleAddToCart(extendOffer, {
                        quantity,
                        modal: Extend.integration.pdpModalOffer,
                        done() {
                            addToCartButton.removeEventListener('click', handleAddToCart, true);
                            addToCartButton.click();
                            addToCartButton.addEventListener('click', handleAddToCart, true);
                            if (dispatchSideCart) {
                                setTimeout(() => window.dispatchEvent(new Event('refreshAjaxSideCart')), 500);
                            }
                        },
                    });
                }

                addToCartButton.addEventListener('click', handleAddToCart, true);
            } catch (err) {
                console.error('EXTEND - PRODUCT OFFER:', err);
            }
        }

        if (!Extend.buttons.instance(extendOffer)) initProductOffer();
    }

    // ============================================================================
    // ATC OFFER — THEME SPECIFIC
    // ============================================================================

    function initATCOffer() {
        // THEME SPECIFIC — update atcSelector to match your theme's quick-buy button
        const atcSelector    = 'button[data-quick-buy]';
        const dispatchSideCart = false;

        function runATC(e) {
            if (!e.target?.closest(atcSelector)) return;

            const el = e.target.closest(atcSelector);
            e.preventDefault();
            e.stopImmediatePropagation();
            document.removeEventListener('click', runATC, true);

            const variantId = el.getAttribute('data-variant-id');
            // const variantId = el.closest('form').querySelector('[name="id"]').value;

            let productCategory, productPrice;
            // OBC — update per merchant if needed
            // meta.products.forEach(each => {
            //     const match = each.variants.find(v => v.id.toString() === variantId.toString());
            //     if (match) { productCategory = each.type; productPrice = match.price; }
            // });

            Extend.modal.open({
                referenceId: variantId,
                price:       productPrice,
                category:    productCategory,
                onClose(plan, product) {
                    if (plan && product) {
                        ExtendShopify.addPlanToCart(
                            { plan, product, quantity: 1 },
                            (err) => { if (err) return; }
                        );
                    }
                    el.click();
                    document.addEventListener('click', runATC, true);
                    if (dispatchSideCart) {
                        setTimeout(() => window.dispatchEvent(new Event('refreshAjaxSideCart')), 500);
                    }
                },
            });
        }

        document.addEventListener('click', runATC, true);
    }

    // ============================================================================
    // QUICKVIEW OFFER — THEME SPECIFIC
    // ============================================================================

    function initQuickviewOffer() {
        // THEME SPECIFIC — implement quickview offer logic here
    }

    // ============================================================================
    // CART OFFER
    // ============================================================================

    function initCartOffer(initialCart) {
        if (!isMainCart && !Extend.integration.sidecartOffer) return;

        cart = initialCart;
        let init = false;

        // -------------------------------------------------------------------------
        // Selectors — THEME SPECIFIC
        // -------------------------------------------------------------------------
        const cartRowItem          = isMainCart ? 'tr.cart-item'          : '';
        const cartRowItemImage     = isMainCart ? '.cart-item__media a'   : '';
        const cartRowItemTitle     = isMainCart ? '.cart-item__details a' : '';
        const cartRowItemQuantity  = isMainCart ? '.quantity__input'      : '';
        const warrantyOriginalMeta = isMainCart ? '.product-option'       : '';
        const warrantyContainer    = isMainCart ? '.cart-item__details'   : '';
        const metadataContainer    = isMainCart ? '.cart-item__details'   : '';
        const quantityWrapper      = isMainCart ? 'quantity-input'        : '';
        const offerClass           = isMainCart ? 'extend-cart-offer'     : 'extend-side-cart-offer';
        const cartEvent            = isMainCart ? 'refreshAjaxCart'       : 'refreshAjaxSideCart';
        const regEx                = /\d+$/;

        // -------------------------------------------------------------------------
        // refreshCart — THEME SPECIFIC
        // -------------------------------------------------------------------------
        async function refreshCart() {
            location.reload();
        }

        // -------------------------------------------------------------------------
        // renderCartOffer
        // -------------------------------------------------------------------------
        async function renderCartOffer(el, variantId, quantity, index) {
            try {
                if (
                    ExtendShopify.warrantyAlreadyInCart(parseInt(variantId), cart.items) ||
                    ExtendShopify.warrantyAlreadyInCart(variantId.toString(), cart.items)
                ) return;

                if ( isMainCart && !Extend.integration.cartOffer)    return;
                if (!isMainCart && !Extend.integration.sidecartOffer) return;

                const productCategory = cart.items[index].product_type;
                const productPrice    = cart.items[index].price;

                const { components } = await ExtendShopify.getBundleComponents({
                    variantId: `gid://shopify/ProductVariant/${variantId}`,
                }) || {};
                const isBundle = components?.length > 0;

                const offerProps = isBundle
                    ? { referenceId: variantId, products: components }
                    : { referenceId: variantId, price: productPrice, category: productCategory };

                Extend.buttons.renderSimpleOffer(el, {
                    ...offerProps,
                    onAddToCart(options) {
                        ExtendShopify.addPlanToCart({
                            plan:    options.plan,
                            product: options.product,
                            ...(isBundle && { products: options.products }),
                            quantity,
                        }, function (err) {
                            if (err) {
                                console.error('EXTEND - onAddToCart:', err);
                            } else {
                                refreshCart();
                            }
                        });
                    },
                });
            } catch (err) {
                console.error('EXTEND - renderCartOffer:', err);
            }
        }

        // -------------------------------------------------------------------------
        // createElement
        // -------------------------------------------------------------------------
        function createElement(product, index) {
            const url       = product.querySelector(cartRowItemTitle)?.href;
            const variantId = url?.match(regEx)?.[0] ?? cart.items[index].id;
            const quantity  = parseInt(product.querySelector(cartRowItemQuantity)?.value) || 1;

            if (!variantId || !quantity) {
                throw new Error(`Exiting - invalid variantId (${variantId}) or quantity (${quantity})`);
            }

            const existingOffer = product.querySelector('.' + offerClass);
            if (existingOffer) {
                if (
                    existingOffer.dataset.extendVariant  !== variantId.toString() ||
                    existingOffer.dataset.extendQuantity !== quantity.toString()
                ) {
                    existingOffer.remove();
                } else {
                    return;
                }
            }

            const container = product.querySelector(warrantyContainer);
            if (!container) throw new Error('Exiting - warrantyContainer unavailable');

            const newOffer = mkEl('div', offerClass);
            newOffer.setAttribute('data-extend-variant',  variantId);
            newOffer.setAttribute('data-extend-quantity', quantity);
            container.append(newOffer);

            renderCartOffer(newOffer, variantId, quantity, index);
        }

        // -------------------------------------------------------------------------
        // handleStyling
        // -------------------------------------------------------------------------
        function handleStyling() {
            document.querySelectorAll(cartRowItem).forEach(function (el, index) {
                try {
                    const title = el.querySelector(cartRowItemTitle);
                    if (!title) throw new Error('Exiting - title unavailable');

                    if (title.innerText.toLowerCase().includes('extend protection')) {
                        title.style.pointerEvents = 'none';

                        const image = el.querySelector(cartRowItemImage);
                        if (!image) throw new Error('Exiting - image unavailable');
                        image.style.pointerEvents = 'none';

                        el.querySelectorAll(warrantyOriginalMeta).forEach(each => each.remove());

                        if (quantityWrapper) {
                            el.querySelectorAll(quantityWrapper).forEach(each => {
                                each.style.opacity       = '75%';
                                each.style.pointerEvents = 'none';
                            });
                        }

                        if (!cart.items[index]) return;

                        const item = cart.items[index];
                        let warrantyProductData, warrantyTermData;

                        if (item.options_with_values?.[1]) {
                            const refIdMatch    = item.options_with_values[0].value.match(/\-\d{5,}/g);
                            warrantyProductData = item.options_with_values[0].value.replace(refIdMatch, '');
                            warrantyTermData    = item.options_with_values[1].value;
                        } else {
                            warrantyProductData = item.properties?.Product;
                            warrantyTermData    = item.properties?.Term;
                        }

                        if (warrantyProductData === 'Covered Product') {
                            const coveredId     = item.properties['_Extend.ProductId'];
                            const coveredItem   = cart.items.find(i => i.id.toString() === coveredId);
                            warrantyProductData = coveredItem?.title ?? warrantyProductData;
                        }

                        const contentContainer = el.querySelector(metadataContainer);
                        if (contentContainer && !el.querySelector('.extend-warranty-info')) {
                            const productName = mkEl('p', 'extend-warranty-info extend-warranty-info-product');
                            productName.innerHTML = `<b>Product: </b>${warrantyProductData}`;
                            productName.setAttribute('data-cy', 'warranty-description-product');

                            const productTerm = mkEl('p', 'extend-warranty-info extend-warranty-info-term');
                            productTerm.innerHTML = `<b>Term: </b>${warrantyTermData}`;
                            productTerm.setAttribute('data-cy', 'warranty-description-term');

                            contentContainer.append(productName, productTerm);
                        }

                    } else {
                        createElement(el, index);
                    }
                } catch (err) {
                    console.error('EXTEND:', err);
                }
            });
        }

        // -------------------------------------------------------------------------
        // initEventListeners
        // -------------------------------------------------------------------------
        function initEventListeners() {
            if (init) return;

            window.addEventListener(cartEvent, () => {
                fetchCart()
                    .then(() => initializeCartOffer())
                    .catch(err => console.error('EXTEND:', err));
            });

            window.addEventListener('refreshSP', refreshCart);
        }

        // -------------------------------------------------------------------------
        // initializeCartOffer
        // -------------------------------------------------------------------------
        function initializeCartOffer() {
            if (Extend.integration.cartBalancing) {
                ExtendShopify.normalizeCart({ cart, balance: true }, function (err, data) {
                    try {
                        if (err) throw new Error('normalizeCart error: ' + err);
                        if (data?.updates) refreshCart();
                    } catch (err) {
                        console.error('EXTEND:', err);
                    }
                });
            }

            handleStyling();
            initEventListeners();
            init = true;
        }

        try {
            initializeCartOffer();
        } catch (err) {
            console.error('EXTEND:', err);
        }
    }

}


// -------------------------------------------------------------------------
// Consent Logic
// -------------------------------------------------------------------------
function waitForConsent(callback) {
    const interval = setInterval(() => {
        const cp = window.Shopify?.customerPrivacy;
        if (!cp) return;

        if (cp.analyticsProcessingAllowed()) {
            clearInterval(interval);
            callback();
        } else {
            clearInterval(interval);
            document.addEventListener("visitorConsentCollected", (event) => {
                if (event.detail.analyticsAllowed) callback();
            });
        }
    }, 100);
}

waitForConsent(runExtend);

